import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const VIEWPORT_MARGIN = 8;

export function clampPanelOffset({ baseRect, desiredOffset, viewport }) {
  const minX = VIEWPORT_MARGIN - baseRect.left;
  const maxX = viewport.width - VIEWPORT_MARGIN - baseRect.right;
  const minY = VIEWPORT_MARGIN - baseRect.top;
  const maxY = viewport.height - VIEWPORT_MARGIN - baseRect.bottom;

  return {
    x: Math.min(Math.max(desiredOffset.x, Math.min(minX, maxX)), Math.max(minX, maxX)),
    y: Math.min(Math.max(desiredOffset.y, Math.min(minY, maxY)), Math.max(minY, maxY)),
  };
}

function useDraggablePanel(active = true) {
  const panelRef = useRef(null);
  const dragStateRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!active) {
      dragStateRef.current = null;
      setIsDragging(false);
      setOffset({ x: 0, y: 0 });
    }
  }, [active]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const desiredOffset = {
        x: dragState.startOffset.x + event.clientX - dragState.startPointer.x,
        y: dragState.startOffset.y + event.clientY - dragState.startPointer.y,
      };
      setOffset(clampPanelOffset({
        baseRect: dragState.baseRect,
        desiredOffset,
        viewport: { width: window.innerWidth, height: window.innerHeight },
      }));
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, []);

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0 || event.target.closest('button')) return;
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!panelRect) return;

    dragStateRef.current = {
      startPointer: { x: event.clientX, y: event.clientY },
      startOffset: offset,
      baseRect: {
        left: panelRect.left - offset.x,
        right: panelRect.right - offset.x,
        top: panelRect.top - offset.y,
        bottom: panelRect.bottom - offset.y,
      },
    };
    setIsDragging(true);
  }, [offset]);

  const panelStyle = useMemo(() => ({
    transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
  }), [offset]);

  return {
    panelRef,
    panelStyle,
    dragHandleProps: { onPointerDown },
    isDragging,
  };
}

export default useDraggablePanel;

